"use client";

import Script from "next/script";
import { flushRedditPixelQueue } from "@/lib/analytics/analytics.reddit";
import { isSentinelValue } from "@/lib/env";

/**
 * Reddit Pixel base code.
 *
 * Loads the pixel stub and calls rdt('init') only. PageVisit and advanced
 * matching (email / externalId) are handled by analytics.reddit.ts via the
 * analytics facade so SPA navigations and logged-in identity are tracked
 * correctly (same pattern as GA4 send_page_view: false).
 *
 * Must be a Client Component so that `process.env.NEXT_PUBLIC_*` is inlined
 * into the client bundle (which entrypoint.sh replaces at container start),
 * rather than evaluated in a statically pre-rendered Server Component at
 * build time when the env vars still hold sentinel placeholder values.
 *
 * Only renders when NEXT_PUBLIC_REDDIT_PIXEL_ID is set and we are not in a
 * test environment, so local/CI Playwright runs never load the real script.
 */
export const RedditTrackingScript = () => {
  const pixelId = process.env.NEXT_PUBLIC_REDDIT_PIXEL_ID;

  if (!pixelId || isSentinelValue(pixelId) || process.env.NODE_ENV === "test") {
    return null;
  }

  return (
    <Script
      id="reddit-pixel"
      strategy="afterInteractive"
      onLoad={flushRedditPixelQueue}
      dangerouslySetInnerHTML={{
        __html: `
          !function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js?pixel_id=${pixelId}";t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);rdt('init','${pixelId}');
          try{window.dispatchEvent(new Event('reddit-pixel-ready'))}catch(e){}
        `,
      }}
    />
  );
};
